// Sprint-101b — Waitlist signup modal.
// Posts to POST /api/waitlist/join. Used from booking modal + landing
// page Notify Me triggers. Prefills email/pincode/state from props.
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { CheckCircle2, Loader2, MapPin } from "lucide-react";
import api from "../services/api";

export default function WaitlistModal({
  open,
  onOpenChange,
  prefillEmail = "",
  prefillPincode = "",
  prefillState = "",
  source = "other",   // booking_modal | landing | other
}) {
  const [email, setEmail] = useState(prefillEmail);
  const [pincode, setPincode] = useState(prefillPincode);
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [success, setSuccess] = useState(null);   // {state, message}

  useEffect(() => {
    if (open) {
      setEmail(prefillEmail || "");
      setPincode(prefillPincode || "");
      setSuccess(null);
      setErrMsg("");
    }
  }, [open, prefillEmail, prefillPincode]);

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validPin = !pincode || /^\d{6}$/.test(pincode);

  const submit = async () => {
    if (!validEmail || !validPin) return;
    setSubmitting(true);
    setErrMsg("");
    try {
      const { data } = await api.post("/waitlist/join", {
        email,
        pincode: pincode || undefined,
        state: prefillState || undefined,
        source,
      });
      setSuccess({ state: data?.state || prefillState || "your area",
                   message: data?.message || `You're on the list.` });
    } catch (e) {
      setErrMsg(
        (typeof e?.response?.data?.detail === "string"
          ? e.response.data.detail
          : null)
        || "Couldn't join the waitlist right now. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="waitlist-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <MapPin className="h-5 w-5 text-orange-500" />
            {success ? "You're on the list" : "Join the waitlist"}
          </DialogTitle>
          {!success && (
            <DialogDescription className="text-sm text-slate-600">
              We&apos;ll email you the moment PickNGo goes live in your state.
            </DialogDescription>
          )}
        </DialogHeader>

        {success ? (
          <div className="py-6 text-center space-y-3" data-testid="waitlist-success">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
            <p className="text-base font-medium text-slate-800">
              We&apos;ll email you when PickNGo launches in {success.state}.
            </p>
            <Button
              onClick={() => onOpenChange(false)}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="waitlist-close-btn"
            >
              Close
            </Button>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="wl-email" className="text-sm">Email <span className="text-red-500">*</span></Label>
              <Input
                id="wl-email"
                type="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="waitlist-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="wl-pin" className="text-sm">
                Pincode <span className="text-slate-400 text-xs">(optional)</span>
              </Label>
              <Input
                id="wl-pin"
                inputMode="numeric"
                maxLength={6}
                value={pincode}
                onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="797001"
                data-testid="waitlist-pincode-input"
              />
              {prefillState && (
                <p className="text-xs text-slate-500" data-testid="waitlist-derived-state">
                  State: <span className="font-medium text-slate-700">{prefillState}</span>
                </p>
              )}
            </div>
            {errMsg && (
              <p className="text-sm text-red-600" data-testid="waitlist-error">{errMsg}</p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
                data-testid="waitlist-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={submitting || !validEmail || !validPin}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="waitlist-submit-btn"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Join waitlist
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
